// Single source of truth for "what can this account do in RLevents?".
//
// Only two audiences sign into RecruitLook Events: organisation admins and
// event staff. Everything else lands on /account-not-set-up — no role guessing.
//
// IMPORTANT: a failed or aborted lookup is NOT "no access". It returns
// status 'unknown' and callers must show a retry banner instead of redirecting.

import {
  loadUserRoles,
  loadOrgMemberships,
  loadEventStaff,
  clearAuthCaches,
} from '@/lib/authQueries';
import {
  ADMIN_LANDING_PATH,
  STAFF_LANDING_PATH,
  NO_ACCESS_LANDING_PATH,
  ADMIN_ROLES,
  STAFF_ROLES,
} from '@/lib/rolePaths';

export type RleventsAccess = {
  status: 'ok' | 'unknown';
  error?: string;
  aborted?: boolean;
  roles: string[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isStaff: boolean;
  hasOrgMembership: boolean;
  landingPath: string;
  allowedPaths: string[];
};

const ADMIN_PATHS = [
  ADMIN_LANDING_PATH,
  '/org-admin-dashboard',
  '/admin',
  '/manage',
  '/staff',
  STAFF_LANDING_PATH,
  '/billing',
];

const STAFF_PATHS = [STAFF_LANDING_PATH, '/staff', '/manage', '/billing'];

export function clearRleventsAccessCache() {
  clearAuthCaches();
}

export async function resolveRleventsAccess(
  userId: string,
  opts: { force?: boolean } = {}
): Promise<RleventsAccess> {
  const force = !!opts.force;

  const [rolesOut, orgOut, staffOut] = await Promise.all([
    loadUserRoles(userId, force),
    loadOrgMemberships(userId, force),
    loadEventStaff(userId, force),
  ]);

  const failed = [rolesOut, orgOut, staffOut].find((o) => o.status !== 'ok') as any;
  if (failed) {
    // We do not know. Never downgrade, never redirect on this.
    return {
      status: 'unknown',
      error: failed.error,
      aborted: !!failed.aborted,
      roles: [],
      isAdmin: false,
      isSuperAdmin: false,
      isStaff: false,
      hasOrgMembership: false,
      landingPath: '',
      allowedPaths: [],
    };
  }

  const roles = (rolesOut as any).data as string[];
  const hasOrgMembership = ((orgOut as any).data as string[]).length > 0;
  const isEventStaff = (staffOut as any).data as boolean;

  const isSuperAdmin =
    roles.includes('rlevents_super_admin') ||
    roles.includes('super_admin') ||
    roles.includes('admin');
  const isAdmin = roles.some((r) => ADMIN_ROLES.includes(r)) || hasOrgMembership;
  const isStaff = roles.some((r) => STAFF_ROLES.includes(r)) || isEventStaff;

  const landingPath = isAdmin
    ? ADMIN_LANDING_PATH
    : isStaff
      ? STAFF_LANDING_PATH
      : NO_ACCESS_LANDING_PATH;

  const allowedPaths = isAdmin ? ADMIN_PATHS : isStaff ? STAFF_PATHS : [NO_ACCESS_LANDING_PATH];

  return {
    status: 'ok',
    roles,
    isAdmin,
    isSuperAdmin,
    isStaff,
    hasOrgMembership,
    landingPath,
    allowedPaths,
  };
}
